import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Spinner } from '@1trading/react';
import type { ColumnDef } from '@tanstack/react-table';
import { Calculator } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DateCell } from '@/components/data-table/cells';
import { CursorPagination, INITIAL_PAGINATION, type PaginationState, registerNextPageCursor } from '@/components/data-table/cursor-pagination';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { FeeRunExclusionsCard } from '@/features/fee-runs/fee-run-exclusions-card';
import { formatChargeDate, formatEurString } from '@/features/fee-runs/fee-run-helpers';
import { FeeRunStatusChip } from '@/features/fee-runs/fee-run-status-chip';
import { useFeeRuns, useRequestFeeRun } from '@/hooks/queries/fees/use-fee-runs';
import { getErrorMessage, isForbiddenError } from '@/lib/api/error';
import type { FeeRun, FeeScope } from '@/services/api/fee-runs';
import { toDateInputValue } from '@/utils/date';

const PAGE_SIZE = 25;
const FETCH_ERROR_TITLE = 'Failed to fetch fee runs';

export const FEE_RUN_DETAIL_ROUTE = '/operations/fee-runs';

const SCOPE_LABEL: Record<FeeScope, string> = { CUSTODY: 'Custody (OTE)', PLATFORM: 'Platform (OTM)' };

const SCOPE_OPTIONS: ReadonlyArray<{ value: FeeScope; label: string }> = [
  { value: 'CUSTODY', label: SCOPE_LABEL.CUSTODY },
  { value: 'PLATFORM', label: SCOPE_LABEL.PLATFORM },
];

/** The fee collection landing: start a calculation for a charge date and browse every run. */
export const FeeRunsPanel = () => {
  const navigate = useNavigate();
  // Each fee type runs on its fixed day of the month; today is the usual charge date
  const [chargeDate, setChargeDate] = useState(() => toDateInputValue(new Date()));
  // No default: custody and platform run on different days, so the operator always chooses
  const [feeScope, setFeeScope] = useState<FeeScope | null>(null);
  const [pagination, setPagination] = useState<PaginationState>(INITIAL_PAGINATION);
  const cursor = pagination.cursors[pagination.currentPage];

  const runsQuery = useFeeRuns({ cursor, limit: PAGE_SIZE });
  const requestMutation = useRequestFeeRun();
  const { data, isPending, isFetching, isError, error, refetch } = runsQuery;
  const nextCursor = data?.cursor;
  const canCalculate = data?.capabilities.canCalculate ?? false;

  useEffect(() => {
    if (nextCursor) setPagination((previous) => registerNextPageCursor(previous, nextCursor));
  }, [nextCursor]);

  const handleCalculate = async () => {
    if (!feeScope) return;
    try {
      const run = await requestMutation.mutateAsync({ chargeDate, feeScope });
      toast.success('Calculation started');
      navigate(`${FEE_RUN_DETAIL_ROUTE}/${encodeURIComponent(run.runId)}`);
    } catch (requestError) {
      toast.error(isForbiddenError(requestError) ? 'You are not authorised to start a fee run' : `Failed to start the fee run: ${getErrorMessage(requestError)}`);
    }
  };

  const columns = useMemo(
    (): ColumnDef<FeeRun, unknown>[] => [
      { accessorKey: 'chargeDate', header: 'Charge date', cell: ({ row }) => <span className="whitespace-nowrap">{formatChargeDate(row.original)}</span> },
      { accessorKey: 'status', header: 'Status', cell: ({ row }) => <FeeRunStatusChip className="text-xs" status={row.original.status} /> },
      { id: 'scope', header: 'Scope', accessorFn: (row) => row.params.feeScope, cell: ({ row }) => SCOPE_LABEL[row.original.params.feeScope] },
      {
        id: 'users',
        header: 'Account holders',
        accessorFn: (row) => row.totals?.users ?? null,
        cell: ({ row }) => <span className="tabular-nums">{row.original.totals?.users ?? '—'}</span>,
      },
      {
        id: 'finalFeeEur',
        header: 'Fees',
        accessorFn: (row) => (row.totals ? Number(row.totals.finalFeeEur) : null),
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{row.original.totals ? formatEurString(row.original.totals.finalFeeEur) : '—'}</span>,
      },
      { accessorKey: 'requestedBy', header: 'Requested by', cell: ({ row }) => <span className="text-xs">{row.original.requestedBy}</span> },
      { accessorKey: 'requestedAt', header: 'Requested', cell: ({ row }) => <DateCell value={row.original.requestedAt} /> },
      { accessorKey: 'approvedBy', header: 'Approved by', cell: ({ row }) => <span className="text-xs">{row.original.approvedBy ?? '—'}</span> },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>New run</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Calculating is free and reversible: it reads balances, activity and terms as of now, and produces the report. Nothing is charged until the run is reviewed and approved.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fee-run-charge-date">Charge date</Label>
              <Input className="w-44" id="fee-run-charge-date" onChange={(event) => setChargeDate(event.target.value)} type="date" value={chargeDate} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="fee-run-scope">Fees</Label>
              <Select onValueChange={(value) => setFeeScope(value as FeeScope)} value={feeScope ?? undefined}>
                <SelectTrigger className="w-56" id="fee-run-scope">
                  <SelectValue placeholder="Choose fee type" />
                </SelectTrigger>
                <SelectContent>
                  {SCOPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="gap-2" disabled={!canCalculate || !chargeDate || !feeScope || requestMutation.isPending} onClick={() => void handleCalculate()} type="button">
              {requestMutation.isPending ? <Spinner className="size-4" /> : <Calculator className="size-4" />}
              Calculate
            </Button>
          </div>
          {data && !canCalculate ? <p className="text-xs text-muted-foreground">Starting a run is limited to the fee operators group.</p> : null}
        </CardContent>
      </Card>

      <FeeRunExclusionsCard />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle>Runs</CardTitle>
          {isFetching && !isPending ? <Spinner className="size-4" /> : null}
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {isError ? <QueryErrorBanner error={error} onRetry={() => refetch()} title={FETCH_ERROR_TITLE} /> : null}
          <DataTable
            columns={columns}
            data={data?.runs}
            emptyMessage="No fee runs yet. Calculate one for a charge date to see who would be charged."
            isLoading={isPending}
            onRowClick={(run) => navigate(`${FEE_RUN_DETAIL_ROUTE}/${encodeURIComponent(run.runId)}`)}
            surface={false}
          />
          {pagination.totalPages > 1 ? (
            <div className="flex justify-end">
              <CursorPagination
                ariaLabel="Fee run pages"
                currentPage={pagination.currentPage}
                onPageChange={(page) => setPagination((previous) => ({ ...previous, currentPage: page }))}
                totalPages={pagination.totalPages}
              />
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
FeeRunsPanel.displayName = 'FeeRunsPanel';
